import { DQ_PRO_PLAN_KEY } from '../../../../utils/constants';

/**
 * The "Unlock a capability" catalogue.
 *
 * Source: PbDQ_Partner Workspace_13Aug2026.pptx, slides 6 and 9
 * (PW-P0-UnlockRequest / PW-ALL-UnlockRequestResponse) — the notes on those
 * slides spell out the dialog in full. Every string below is taken from them
 * verbatim, including the three section headings and the footer note.
 *
 * The plan keys are the ones that already exist in the plans collection, so a
 * row here names a real entitlement rather than a label:
 *   enterprise_p0   COS Children Assessment
 *   enterprise_p1a  COS Readiness Solution — Provider-Created Programs
 *   enterprise_p1b  COS Readiness Solution — Teacher-Created Programs
 *   enterprise_lc   LearnCreator
 *
 * Rows in CAPABILITY_ACCESS and RESTRICTED_ACCESS are NOT plans — they are
 * feature entitlements, so they carry a `featureKey` instead of a `planKey`.
 */

export const UNLOCK_DIALOG_COPY = {
    title: 'Unlock a capability',
    subtitle: 'Choose what you would like to add to your workspace. Requests are reviewed by DQ before access is granted.',
    currentAccessLabel: 'Current access',
    footnote: 'Looking to add licenses or credits? Use Assign Licenses & Credits instead — unlocking is only for capabilities you do not currently have.',
    confirmCta: 'Continue',
    cancelCta: 'Cancel',
};

/**
 * SOLUTION UPGRADES — the plan a partner can move onto.
 *
 * Slide 6 lists a single combined "COS Readiness Solution"; slide 9 splits it
 * into the Provider-Created and Teacher-Created variants. The split version is
 * used because those are the two plans that actually exist (p1a / p1b), and a
 * single combined row could not resolve to one of them.
 */
export const SOLUTION_UPGRADES = [
    {
        planKey: DQ_PRO_PLAN_KEY.ENTERPRISE_P0,
        name: 'COS Children Assessment',
        desc: 'Measure readiness, risks, and support needs before planning learning.',
    },
    {
        planKey: DQ_PRO_PLAN_KEY.ENTERPRISE_LC,
        name: 'LearnCreator',
        desc: 'Create customized learning programs for the schools you support.',
    },
    {
        planKey: DQ_PRO_PLAN_KEY.ENTERPRISE_P1A,
        name: 'COS Readiness Solution — Provider-Created Programs',
        desc: 'Assessment plus programs you create centrally and distribute to your schools.',
    },
    {
        planKey: DQ_PRO_PLAN_KEY.ENTERPRISE_P1B,
        name: 'COS Readiness Solution — Teacher-Created Programs',
        desc: 'Assessment plus LearnCreator for schools, so teachers build their own programs.',
    },
];

/** CAPABILITY ACCESS — dashboards, requested rather than purchased outright. */
export const CAPABILITY_ACCESS = [
    {
        featureKey: 'network_dashboard',
        name: 'Network Dashboard',
        desc: 'Aggregated assessment and learning insights across schools in a network.',
    },
    {
        featureKey: 'national_dashboard',
        name: 'National Dashboard',
        desc: 'National-level reporting across participating schools and networks.',
        // Slide 9 marks this one "Review required" — it is requestable, but not
        // granted automatically the way a paid upgrade is.
        badge: 'Review required',
    },
];

/**
 * RESTRICTED ACCESS — requested like the rest, reviewed by DQ.
 *
 * PW deck slide 9: "Government Checklist check and continue: Popup + System
 * Email". Button Registry BTN-0018/BTN-0019 describe it as an access request
 * for national governments, decided by a DQ administrator — which is what
 * every partner request here is. The COS Government Checklist page's Request
 * Access opens this dialog with the row already picked.
 */
export const RESTRICTED_ACCESS = [
    {
        featureKey: 'government_checklist',
        name: 'COS Government Checklist',
        desc: 'For national governments and government-designated organizations.',
        badge: 'Restricted',
    },
];

export const UNLOCK_SECTIONS = [
    { heading: 'SOLUTION UPGRADES', rows: SOLUTION_UPGRADES },
    { heading: 'CAPABILITY ACCESS', rows: CAPABILITY_ACCESS },
    { heading: 'RESTRICTED ACCESS', rows: RESTRICTED_ACCESS },
];


/*
 * ── School / School Network view ────────────────────────────────────────────
 *
 * Source: PbDQ_School Workspace_13Aug2026.pptx slides 6-8 (SW-P0 / SW-LC
 * UnlockRequest, SW-P0, LC-UnlockRequestResponse) and PbDQ_Network
 * Workspace_13Aug2026.pptx slides 5-7 (the SNW equivalents). Both decks draw
 * the same dialog, with "DQ" in the subtitle annotated "DQ → your partner".
 *
 * Three rows and no footnote. COS Readiness is ONE row here: whether the
 * school gets Provider- or Teacher-Created programs is the partner's call when
 * it approves, not the school's.
 *
 * `planKey` is the key the API takes (CLIENT_UNLOCK_CAPABILITIES in the API).
 * Which rows are active comes from GET unlock-request/options — the school's
 * own product access, not the plan on its session (that is the partner's).
 */
export const UNLOCK_DIALOG_COPY_CLIENT = {
    ...UNLOCK_DIALOG_COPY,
    subtitle: 'Choose what you would like to add to your workspace. Requests are reviewed by your partner before access is granted.',
    // The client slides carry no licences-and-credits footnote.
    footnote: '',
    activeBadge: 'Active',
};

export const SOLUTION_UPGRADES_CLIENT = [
    {
        planKey: DQ_PRO_PLAN_KEY.ENTERPRISE_P0,
        name: 'COS Children Assessment',
        desc: 'Measure readiness, risks, and support needs before planning learning.',
    },
    {
        planKey: DQ_PRO_PLAN_KEY.ENTERPRISE_LC,
        name: 'LearnCreator',
        desc: 'Create customized learning programs for the schools you support.',
    },
    {
        planKey: DQ_PRO_PLAN_KEY.ENTERPRISE_P1,
        name: 'COS Readiness Solution',
        desc: 'Connect assessment to learning and back again: results identify what students need, programs respond to it, and reassessment shows what changed.',
        badge: 'Upgrade',
    },
];

export const UNLOCK_SECTIONS_CLIENT = [
    { heading: 'SOLUTION UPGRADES', rows: SOLUTION_UPGRADES_CLIENT },
];

/**
 * The confirmation after Continue — the right-hand panel of
 * SW / SNW-P0, LC-UnlockRequestResponse. The slide strikes out its Request
 * and Status rows, so only Submitted remains.
 */
export const UNLOCK_SUBMITTED_COPY = {
    title: 'Request Submitted',
    body: (name) => `Your request for ${name} has been received.`,
    note: "We'll notify you when the request has been reviewed.",
    submittedLabel: 'Submitted',
    doneCta: 'Done',
};

/**
 * Pick the sections and copy for whoever is looking.
 *
 * @param {boolean} isClientWorkspace true for a school or a school network
 */
export const unlockViewFor = (isClientWorkspace) => (isClientWorkspace
    ? { sections: UNLOCK_SECTIONS_CLIENT, copy: UNLOCK_DIALOG_COPY_CLIENT }
    : { sections: UNLOCK_SECTIONS, copy: UNLOCK_DIALOG_COPY });
